<script setup lang="ts">
  import { ref } from 'vue';

  import type { Note } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { toast } from '@ui/components/sonner';
  import { Timestamp } from '@ui/components/time';

  import { UserAvatar } from '@/components/users';
  import { useNotes } from '@/store/notes';

  import NoteEditorDialog from './NoteEditorDialog.vue';

  interface Props {
    note: Note;
  }

  const props = defineProps<Props>();

  const isEditorOpen = ref(false);

  const notesStore = useNotes();

  const pinNote = async (event: Event) => {
    event.stopPropagation(); // Prevent note editor from coming up when clicking pin button
    try {
      await notesStore.updateNote(props.note, { pinned: !props.note.pinned });
    } catch (error: any) {
      toast.error(error.clientMessage);
    }
  };
</script>

<template>
  <div
    class="py-2 text-left cursor-pointer relative flex flex-col justify-between gap-2"
    @click="isEditorOpen = true"
  >
    <div class="flex flex-col gap-y-2">
      <div class="flex items-start gap-x-3">
        <UserAvatar class="mt-1.5" size="sm" :user="note.author" />

        <div class="flex items-start flex-1 gap-x-4">
          <div class="flex flex-col items-start flex-1 gap-y-1">
            <p class="line-clamp-2 text-xs text-ellipsis">{{ note.content }}</p>

            <Timestamp
              v-if="note.author"
              class="text-muted-foreground"
              :time="note.created_at"
              local
              show-from-now
              date-only
              format="L"
              with-conversion-table
              :open-delay="200"
            />
          </div>

          <Button
            class="w-auto h-auto p-2 -mt-1 -mr-0.5"
            variant="transparent"
            size="icon"
            @click.prevent="pinNote"
          >
            <i
              class="far fa-thumbtack text-base! fa-rotate-by"
              style="--fa-rotate-angle: 50deg"
              :class="{ 'fas text-warning-700': note.pinned }"
            />
          </Button>
        </div>
      </div>
    </div>
  </div>

  <NoteEditorDialog
    v-model:is-open="isEditorOpen"
    :edit-note-props="{
      note,
      partnerId: note.partner_id,
    }"
  />
</template>

<style scoped></style>
